import React from "react";
import { Alert, Box, Button, CircularProgress, Grid, Snackbar } from "@mui/material";
import MainLayout from "../../components/layouts/MainLayout";
import EnrichmentGrid from "../Enrichment/EnrichmentGrid";
import { savedSearchGridContext } from "../Enrichment/gridPaidRun";
import { FilterControllerProvider } from "../Search/FilterBar/FilterControllerContext";
import DeleteSearchDialog from "../Search/DeleteSearchDialog";
import SaveSearchDialog from "../Search/SaveSearchDialog";
import SearchResultsPagination from "../Search/SearchResultsPagination";
import SavedSearchDetailChrome from "./SavedSearchDetailChrome";
import SavedSearchHistoricalResults from "./SavedSearchHistoricalResults";
import WorkspaceEnrichPanelV2 from "./WorkspaceEnrichPanelV2";
import CompanyResultsEmptyState, { SAVED_SEARCH_EMPTY } from "../../components/EmptyState/CompanyResultsEmptyState";
import { SavedSearchExportDialog } from "./useSavedSearchExport";
import { Wrapper } from "./workspaceGridComponents";
import { FIXED_HEADER_OFFSET } from "../../components/layouts/headerLayout";
import { getBannerGradientExtended, getBannerGradientSoft } from "../../components/layouts/SubHeader/subHeaderBannerStyles";

const CONTENT_SX = {
  padding: { xs: "0px 16px 24px 16px", md: "0px 40px 32px 40px" },
};

function SavedSearchDetailView({
  filterSectionProps, workspace, bannerCount, isSavedSearchDirty,
  savedSearchOption, setDeleteDialogOpen, setOpenSaveSearchDialog, saveSearchDisabled,
  savedSearchExport, filtersExpanded, setFiltersExpanded, resultsSource,
  runSavedQuery, originalSavedFilters, searchLoading, fetchWorkspaceDetail,
  error, batchRecovery, enrichmentStickyShellRef, enrichQuery, setEnrichQuery,
  columnTitle, setColumnTitle, llmProvider, setLlmProvider,
  enriching, batchStatus, isDeleting, showingWorkspaceSnapshot,
  selectedIds, enrichScopeIds, handleEnrich, enrichQuote,
  workspaceEnrichmentColumns, workspaceEnrichmentColumnLabels, isMobile, enrichPanelCollapsed,
  setEnrichPanelCollapsed, id, companies, showResultsEmptyState, enrichmentGridRef,
  newCompanyIds = null, flashNewResultRows = false,
  handleSpreadsheetSelectionChange, workspaceEnrichmentStatusMap, workspaceEnrichmentSummaryStatusMap, focusGeneratedColumnKey,
  snapshotPagination = null, historicalRows, historicalColumns, historicalLoading, historicalError, loadMoreHistorical,
  handleFocusGeneratedColumnConsumed, deleteDialogOpen, handleDelete, openSaveSearchDialog,
  activeQuery, handleSavedSearchCreated, saveSearchSnackbar, setSaveSearchSnackbar, onboardingBanner = null, livePager = null,
}) {
  // #670: a grid run on the stored snapshot is quoted and attached to this saved search.
  const gridContext = savedSearchGridContext({ savedSearchId: id, showingSnapshot: showingWorkspaceSnapshot });
  return (
    <MainLayout>
      <Wrapper data-testid="saved-search-detail">
        <FilterControllerProvider {...filterSectionProps}>
          <SavedSearchDetailChrome
            workspace={workspace}
            bannerCount={bannerCount}
            isDirty={isSavedSearchDirty}
            savedSearchOption={savedSearchOption}
            onDelete={() => setDeleteDialogOpen(true)}
            onSaveSearch={() => setOpenSaveSearchDialog(true)}
            saveSearchDisabled={saveSearchDisabled}
            onExport={savedSearchExport.onExport}
            exportDisabled={savedSearchExport.exportDisabled} exportTitle={savedSearchExport.exportTitle}
            filterBarProps={filterSectionProps}
            continueLavenderBand
            collapseFilters
            filtersExpanded={filtersExpanded}
            onToggleFilters={() => setFiltersExpanded((current) => !current)}
            resultsSource={resultsSource}
            sourceActions={{
              runSavedQuery,
              runSavedQueryDisabled: !originalSavedFilters || searchLoading,
              returnToStoredSnapshot: fetchWorkspaceDetail,
            }}
          />

          <Grid
            container
            sx={{
              ...CONTENT_SX,
              // Pull content into the lavender band so the wash continues behind Enrich.
              background: (theme) => getBannerGradientExtended(theme),
            }}
          >
            <Grid item xs={12}>
              {batchRecovery?.error ? <Alert severity="warning" sx={{mb: 2}}
                action={<Button color="inherit" onClick={batchRecovery.retry}>Retry status check</Button>}>
                {batchRecovery.error}
              </Alert> : null}
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : null}
              {onboardingBanner}

              <Box data-testid="workspace-enrichment-sticky-scope">
                <Box
                  ref={enrichmentStickyShellRef}
                  data-testid="workspace-enrichment-sticky-shell"
                  sx={{
                    position: "sticky",
                    top: FIXED_HEADER_OFFSET,
                    zIndex: 8,
                    // Extend banner lavender behind the white card, then fade to page.
                    pt: 0.5,
                    pb: 2.5,
                    mb: 2,
                    background: (theme) => getBannerGradientSoft(theme),
                    borderRadius: 0,
                  }}
                >
                  <WorkspaceEnrichPanelV2
                    variant="v3"
                    enrichQuery={enrichQuery}
                    setEnrichQuery={setEnrichQuery}
                    columnTitle={columnTitle} setColumnTitle={setColumnTitle}
                    llmProvider={llmProvider}
                    setLlmProvider={setLlmProvider}
                    enriching={enriching}
                    batchStatus={batchStatus}
                    disabled={isDeleting || !showingWorkspaceSnapshot}
                    selectedCount={selectedIds.size > 0 ? enrichScopeIds.length : 0} enrichmentScopeCount={enrichScopeIds.length}
                    onEnrich={handleEnrich} enrichQuote={enrichQuote}
                    workspaceEnrichmentColumns={workspaceEnrichmentColumns} workspaceEnrichmentColumnLabels={workspaceEnrichmentColumnLabels}
                    {...(isMobile
                      ? {
                          collapsed: enrichPanelCollapsed,
                          onExpand: () => setEnrichPanelCollapsed(false),
                        }
                      : {})}
                  />
                </Box>

                {searchLoading && companies.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : showResultsEmptyState ? (
                  <CompanyResultsEmptyState variant={SAVED_SEARCH_EMPTY} />
                ) : (
                  isMobile ? (
                    <Box
                      ref={enrichmentGridRef}
                      data-testid="enrichment-grid-section"
                      sx={{
                        width: "100%",
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        scrollMarginTop: FIXED_HEADER_OFFSET,
                      }}
                    >
                      <EnrichmentGrid
                        companies={companies} workspaceId={id} searchEnrichmentContext={gridContext}
                        crmSourceDirty={isSavedSearchDirty}
                        selectedRows={selectedIds}
                        onSelectedRowsChange={handleSpreadsheetSelectionChange}
                        workspaceEnrichmentColumns={workspaceEnrichmentColumns} workspaceEnrichmentColumnLabels={workspaceEnrichmentColumnLabels}
                        workspaceEnrichmentStatusMap={workspaceEnrichmentStatusMap}
                        workspaceEnrichmentSummaryStatusMap={workspaceEnrichmentSummaryStatusMap}
                        focusGeneratedColumnKey={focusGeneratedColumnKey}
                        onFocusGeneratedColumnConsumed={handleFocusGeneratedColumnConsumed}
                        newCompanyIds={newCompanyIds} flashNewResultRows={flashNewResultRows}
                      />
                    </Box>
                  ) : (
                    <Box ref={enrichmentGridRef} data-testid="enrichment-grid-section">
                      <EnrichmentGrid
                        companies={companies} workspaceId={id} searchEnrichmentContext={gridContext}
                        crmSourceDirty={isSavedSearchDirty}
                        selectedRows={selectedIds}
                        onSelectedRowsChange={handleSpreadsheetSelectionChange}
                        workspaceEnrichmentColumns={workspaceEnrichmentColumns} workspaceEnrichmentColumnLabels={workspaceEnrichmentColumnLabels}
                        workspaceEnrichmentStatusMap={workspaceEnrichmentStatusMap}
                        workspaceEnrichmentSummaryStatusMap={workspaceEnrichmentSummaryStatusMap}
                        focusGeneratedColumnKey={focusGeneratedColumnKey}
                        onFocusGeneratedColumnConsumed={handleFocusGeneratedColumnConsumed}
                        newCompanyIds={newCompanyIds} flashNewResultRows={flashNewResultRows}
                      />
                    </Box>
                  )
                )}
                {snapshotPagination ? <SearchResultsPagination {...snapshotPagination} /> : null}
                {!showingWorkspaceSnapshot && livePager && !showResultsEmptyState ? (
                  <SearchResultsPagination {...livePager} />
                ) : null}
                {showingWorkspaceSnapshot && workspace?.id === id && workspace?.record_type === "saved_search" ? (
                  <SavedSearchHistoricalResults
                    key={id}
                    rows={historicalRows}
                    columns={historicalColumns}
                    totalCount={workspace?.previously_enriched_company_count || 0}
                    loading={historicalLoading}
                    error={historicalError}
                    onLoadMore={loadMoreHistorical}
                    onExportSelected={savedSearchExport.onExportHistorical}
                  />
                ) : null}
              </Box>
            </Grid>
          </Grid>
        </FilterControllerProvider>

        <DeleteSearchDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          searchName={workspace?.name}
          isDeleting={isDeleting}
        />

        <SaveSearchDialog
          open={openSaveSearchDialog}
          onClose={() => setOpenSaveSearchDialog(false)}
          searchSpec={{
            query: activeQuery,
            pagination: { page_size: 25, cursor: null },
          }}
          persistMode="workspace"
          onSaveSuccess={handleSavedSearchCreated}
        />
        <SavedSearchExportDialog exportState={savedSearchExport} />

        <Snackbar
          open={saveSearchSnackbar.open}
          autoHideDuration={3500}
          onClose={() =>
            setSaveSearchSnackbar((current) => ({ ...current, open: false }))
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() =>
              setSaveSearchSnackbar((current) => ({ ...current, open: false }))
            }
            sx={{ width: "100%" }}
          >
            {saveSearchSnackbar.message}
          </Alert>
        </Snackbar>
      </Wrapper>
    </MainLayout>
  );
}

export { SavedSearchDetailView, CONTENT_SX };
